import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  benefitsText,
  CUSTOM_MAX_CENTS,
  CUSTOM_MIN_CENTS,
  CUSTOM_PLAN,
  customAmountError,
  customPlan,
  formatCredits,
  formatEur,
  parseEurToCents,
  STUDIO_MIN_CENTS,
} from "@/lib/plans";

const DEFAULT_AMOUNT = "50";

/**
 * "Build your own" plan: the customer types a price and sees what it buys
 * before checkout. The server recomputes all of it from the amount.
 */
export default function CustomPlanCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [amount, setAmount] = useState(DEFAULT_AMOUNT);

  const cents = parseEurToCents(amount);
  const error = customAmountError(t, cents);
  const preview = useMemo(
    () => (cents !== null && !error ? customPlan(cents) : null),
    [cents, error]
  );

  const start = () => {
    if (!preview) return;
    const euros = (preview.amountCents / 100).toFixed(2);
    navigate(
      `/checkout?plan=${encodeURIComponent(CUSTOM_PLAN)}&amount=${encodeURIComponent(euros)}`
    );
  };

  return (
    <div className="relative mx-auto mt-12 w-full max-w-xl rounded-2xl border-2 border-primary-300 bg-background-50 p-6 md:p-8 shadow-lg">
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-4 py-1 text-xs font-semibold text-background-50 shadow-sm">
        {t("pricing.custom.badge")}
      </span>

      <h3 className="text-center text-xl font-bold text-foreground-950">{t("pricing.custom.title")}</h3>
      <p className="mt-2 text-center text-sm text-foreground-600">
        {t("pricing.custom.intro", {
          min: formatEur(CUSTOM_MIN_CENTS),
          max: formatEur(CUSTOM_MAX_CENTS),
          studio: formatEur(STUDIO_MIN_CENTS),
        })}
      </p>

      <div className="mt-6 rounded-xl bg-background-100 p-4 md:p-5">
        <label htmlFor="custom-amount" className="block text-sm font-semibold text-foreground-800">
          {t("pricing.custom.priceLabel")}
        </label>
        <div className="relative mt-2">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-foreground-500">
            €
          </span>
          <input
            id="custom-amount"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") start();
            }}
            aria-invalid={Boolean(error)}
            aria-describedby="custom-amount-help"
            className={`w-full rounded-lg border bg-background-50 py-3 pl-9 pr-4 text-xl font-bold text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 ${
              error ? "border-red-300" : "border-background-300/60"
            }`}
          />
        </div>
        <p id="custom-amount-help" className="mt-3 text-sm" aria-live="polite">
          {preview ? (
            <span className="text-foreground-700">
              <Trans
                i18nKey="pricing.custom.summary"
                count={preview.credits}
                values={{
                  amount: formatEur(preview.amountCents),
                  formatted: formatCredits(preview.credits),
                  level: preview.level.name,
                }}
                components={{
                  amount: <span className="font-bold text-primary-600" />,
                  credits: <span className="font-bold text-primary-600" />,
                }}
              />
            </span>
          ) : (
            <span className="text-red-600">{error}</span>
          )}
        </p>
      </div>

      {preview && (
        <dl className="mt-5 divide-y divide-background-200/70 text-sm">
          <div className="flex items-center justify-between py-3">
            <dt className="text-foreground-600">{t("pricing.custom.credits")}</dt>
            <dd className="font-semibold text-foreground-950">{formatCredits(preview.credits)}</dd>
          </div>
          <div className="flex items-center justify-between py-3">
            <dt className="text-foreground-600">{t("pricing.custom.level")}</dt>
            <dd className="font-semibold text-primary-600">{preview.level.name}</dd>
          </div>
          {benefitsText(t, preview.level).map((benefit) => (
            <div key={benefit} className="flex items-center justify-between py-3">
              <dt className="text-foreground-600">{benefit}</dt>
              <dd>
                <i className="ri-checkbox-circle-fill text-lg text-primary-500" aria-label={t("pricing.custom.included")}></i>
              </dd>
            </div>
          ))}
        </dl>
      )}

      <button
        type="button"
        onClick={start}
        disabled={!preview}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 py-3.5 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100 cursor-pointer"
      >
        {t("pricing.custom.cta")}
        <i className="ri-arrow-right-line"></i>
      </button>
    </div>
  );
}
